import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  firstVerseNumber,
  getFirstChapterVerse,
  isNewTestament,
} from '@deaf-bible-society-2-0/deafbible-metadata-utils';
import {
  setExpandedItem,
  setShowSelectorList,
  setShowTranslationsDropdown,
  setShowCountriesDropdown,
  setSelectedTestament,
} from '../store/interface';
import {
  setCBTParams,
  setSelectedChapterVerse,
  setSelectedBook,
  setCode,
  setIsEmbedded,
  setTextVersion,
} from '../store/params';

import { listTranslations } from '../store/translations/selectors';
import { RootState } from '../store';
import { lookupTable } from '../store/translation/selectors';
import {
  getSelectedTranslation,
  setLastURL,
  setPrevTextVersion,
  setSelectedTranslation,
  getPrevTextVersion,
} from '../utils/localStorage';
import useHomeCountryCode from './useHomeCountryCode';
import { setSelectedCountry } from '../store/translations';
import { getFirstCBTVideoSuffixUrl } from '../utils/urlState';
import useCountries from './useCountries';
import { getURLParams } from '../utils/history';
import getTimestamps from '../utils/api/getTimestamps';
import { getBBBVideoId } from '../utils/videoHelper';
import { findChapterIntroOwner, introUrlVerseForVideo } from '../utils/introResolver';
import { hasIntroSegment, isSegmentIntroVerse, selectionVerseFromUrl } from '../utils/timestamp';

const blacklist = ['feedback', 'embed', 'watch-plans', 'plan-video'];
const DEFAULT_VERSION = 'kjv_en';

export default (): void => {
  const history = useHistory();
  const dispatch = useDispatch();

  const homeCountryCode = useHomeCountryCode();
  const { selectCountry } = useCountries();

  const { code, bookOrSlug, chapter, verse, cbtVideoType, embed, textVersion } = getURLParams();

  useEffect(() => {
    dispatch(setIsEmbedded(embed));
  }, [dispatch, embed]);

  useEffect(() => {
    if (textVersion !== undefined) {
      // If text version is in URL, use it
      setPrevTextVersion(textVersion);
      dispatch(setTextVersion(textVersion));
    } else {
      // If no text version in URL, check if this is a first-time user
      const prevVersion = getPrevTextVersion();
      if (!prevVersion) {
        // For first-time users or when localStorage is cleared, set a default version
        setPrevTextVersion(DEFAULT_VERSION);
        dispatch(setTextVersion(DEFAULT_VERSION));
        // Update URL to include the default version
        const url = new URL(window.location.toString());
        url.searchParams.set('text', DEFAULT_VERSION);
        history.replace(`${url.pathname}${url.search}`);
      } else {
        // If user has previously selected a version but it's not in URL,
        // this means they intentionally want to hide the text
        dispatch(setTextVersion(undefined));
      }
    }
  }, [dispatch, textVersion, history]);

  const translationsByCountry = useSelector((state: RootState) => state.translations.rawData);

  const lut = useSelector(lookupTable);
  const activeBook = useSelector((state: RootState) => state.params.bookOrSlug);
  const activeChapter = useSelector((state: RootState) => state.params.chapter);
  const activeVerse = useSelector((state: RootState) => state.params.verse);
  const cbtVideos = useSelector((state: RootState) => state.translation.rawData?.stories);

  const selectedTranslationType = useSelector(
    (state: RootState) => state.translation.rawData?.translation_type
  );

  const listTrans = useSelector(listTranslations);

  const getCountryByTranslation = useCallback(
    (translationCode: string | undefined): string | undefined =>
      translationsByCountry?.find(translation =>
        translation.translations.some(tran => tran.translation_shortcode === translationCode)
      )?.countryCode,
    [translationsByCountry]
  );

  const isOnBlacklist = useCallback(
    (translationCode: string | undefined) =>
      blacklist.some(item => translationCode?.includes(item)),
    []
  );

  /**
   *
   * URL structure: /<translationCode>/<ref>/[cbtVideoType]
   *
   * if BBB, <ref> format is <book>.<chapter>.<verse>. Example: GEN.1.1
   *
   * If CBT, <ref> is the slug of the story. Example: god-creates-everything
   *
   * In addition, [cbtVideoType] is the specific video of the story group that was selected.
   *
   * Full CBT URL example: /god-creates-everything/intro
   *
   */

  /**
   * Deals with the <translationCode> part of URL.
   *
   * 1. After fetching list of translations, check whether if translationCode in /<translationCode> exists.
   * 2. If it is empty or doesn't match any code from the list, check if selectedTranslaton exists in localStorage.
   * 3. If selectedTranslation exists in localStorage, use this and replace top of navigation stack with /<selectedTranslation>
   * 4. If it doesn't exist, check countryCode from geoip service
   * 5.
   *
   */
  useEffect(() => {
    if (isOnBlacklist(code)) {
      // ignore paths on the blacklist.
      return;
    }

    if (listTrans !== undefined && listTrans.length > 0) {
      if (listTrans.some(translation => translation.translation_shortcode === code)) {
        dispatch(setCode(code));
        dispatch(setSelectedCountry(getCountryByTranslation(code)));
        setSelectedTranslation(code);
        return;
      }

      if (!listTrans.some(translation => translation.translation_shortcode === code)) {
        // check local storage
        const selectedTranslation = getSelectedTranslation();
        if (selectedTranslation) {
          history.replace(`/${selectedTranslation}`);
          return;
        }

        if (homeCountryCode !== undefined) {
          const translations = translationsByCountry?.find(
            data => data.countryCode === homeCountryCode
          );

          if (translations !== undefined) {
            dispatch(setSelectedCountry(homeCountryCode));
            selectCountry(homeCountryCode);

            if (translations.translations.length > 1) {
              dispatch(setShowTranslationsDropdown(true));
              return;
            }

            if (translations.translations.length === 1) {
              history.replace(`/${translations.translations[0].translation_shortcode}`);
              return;
            }
          }

          dispatch(setShowCountriesDropdown(true));
        }
      }
    }
  }, [
    code,
    dispatch,
    getCountryByTranslation,
    history,
    selectCountry,
    homeCountryCode,
    isOnBlacklist,
    listTrans,
    translationsByCountry,
  ]);

  useEffect(() => {
    if (isOnBlacklist(code)) {
      // ignore paths on the blacklist.
      return;
    }
    if (code && selectedTranslationType === 'CBT' && cbtVideos !== undefined) {
      const cbtVideoMeta = cbtVideos.find(story => story.slug === bookOrSlug);

      if (
        cbtVideoMeta !== undefined &&
        cbtVideoMeta.videos.some(video => video.type === cbtVideoType && video.hls !== null)
      ) {
        dispatch(setExpandedItem(bookOrSlug));
        dispatch(setSelectedBook(bookOrSlug));
        dispatch(setCBTParams({ slug: bookOrSlug, type: cbtVideoType }));
        setLastURL(`/${code}/${bookOrSlug}/${cbtVideoType}`);
      } else {
        dispatch(setCode(code));
        dispatch(setShowSelectorList(true));
        history.replace(`/${code}${getFirstCBTVideoSuffixUrl(cbtVideos)}`);
      }
    }
  }, [
    bookOrSlug,
    cbtVideoType,
    cbtVideos,
    code,
    dispatch,
    history,
    isOnBlacklist,
    selectedTranslationType,
  ]);

  useEffect(() => {
    if (!isOnBlacklist(code) && selectedTranslationType === 'BBB' && lut !== undefined) {
      const syncBBBParams = async () => {
        const urlSelectionVerse = selectionVerseFromUrl(verse) ?? verse ?? '';
        const isSegmentIntro = isSegmentIntroVerse(verse);

        const [validatedBook, validatedChapter, validatedVerse] = lut.validateReference(
          bookOrSlug ?? '',
          chapter ?? '',
          urlSelectionVerse
        );

        const isReferenceNormalized =
          validatedBook !== bookOrSlug ||
          validatedChapter !== chapter ||
          (!isSegmentIntro && validatedVerse !== verse) ||
          (isSegmentIntro && validatedVerse !== urlSelectionVerse);

        // A verse that gets normalized while we're already playing this exact
        // book + chapter is an out-of-range playback overshoot (e.g. a trailing
        // end marker like 11:58 in a 57-verse chapter). Leave the URL alone so
        // the video can reach its end and auto-advance, instead of being pulled
        // back to the chapter intro.
        const isPlaybackOvershoot =
          verse !== undefined &&
          verse !== '0' &&
          !isSegmentIntro &&
          validatedVerse !== verse &&
          validatedBook === bookOrSlug &&
          validatedChapter === chapter &&
          activeBook === validatedBook &&
          activeChapter === validatedChapter;

        if (isPlaybackOvershoot) {
          return;
        }

        // Mid-chapter segment intro (e.g. "21.0"): keep the URL form and select
        // the video via the anchor verse ("21").
        if (isSegmentIntro && !isReferenceNormalized) {
          dispatch(setExpandedItem(validatedBook));
          dispatch(setSelectedTestament(isNewTestament(validatedBook) ? 'new' : 'old'));
          dispatch(setSelectedBook(validatedBook));
          dispatch(
            setSelectedChapterVerse({
              chapter: validatedChapter,
              verse: urlSelectionVerse,
            })
          );
          setLastURL(
            `/${code}/${validatedBook}.${validatedChapter}.${verse}${window.location.search}`
          );
          return;
        }

        let verseForUrl = isSegmentIntro ? `${validatedVerse}.0` : validatedVerse;
        // Verse used to select the playing video. Normally the validated verse,
        // but for an intro we point it at the chapter's intro-owning video so the
        // correct video is selected even when verse 1 lives in a different video.
        let selectionVerse = validatedVerse;
        const chapterNumber = parseInt(validatedChapter, 10);

        // Direct links to a video's first numbered verse (e.g. /MRK.15.21) should
        // open that video's intro when it has one. Skip when Redux already matches
        // so leaving intro for the range button (21.0 → 21) is not bounced back.
        if (
          !isSegmentIntro &&
          !Number.isNaN(chapterNumber) &&
          code !== undefined &&
          validatedVerse !== '0'
        ) {
          const targetVideo = lut.lookupByReference(
            validatedBook,
            validatedChapter,
            validatedVerse
          );
          const videoFirstVerse =
            targetVideo !== undefined ? firstVerseNumber(targetVideo, validatedChapter) : undefined;
          const alreadyOnThisVideoContent =
            activeBook === validatedBook &&
            activeChapter === validatedChapter &&
            activeVerse === validatedVerse;

          if (
            targetVideo !== undefined &&
            videoFirstVerse === validatedVerse &&
            !alreadyOnThisVideoContent
          ) {
            try {
              const timestamps = await getTimestamps(
                code,
                validatedBook,
                getBBBVideoId(targetVideo),
                targetVideo.bbb_id,
                targetVideo.chapter
              );

              if (hasIntroSegment(timestamps, chapterNumber, targetVideo.bbb_id)) {
                const chapterVideos = lut.videosInChapter(validatedBook, validatedChapter) ?? [
                  targetVideo,
                ];
                verseForUrl = introUrlVerseForVideo(targetVideo, validatedChapter, chapterVideos);
                selectionVerse = validatedVerse;
              }
            } catch (error) {
              // Keep validated verse when timestamp fetch fails.
            }
          }
        }

        const shouldCheckVerseZero =
          verse === '0' || verse === undefined || (isReferenceNormalized && !isSegmentIntro);

        if (
          !Number.isNaN(chapterNumber) &&
          code !== undefined &&
          shouldCheckVerseZero &&
          verseForUrl === validatedVerse
        ) {
          try {
            const introOwner = findChapterIntroOwner(lut, validatedBook, validatedChapter);
            const targetVideo =
              introOwner ?? lut.lookupByReference(validatedBook, validatedChapter, validatedVerse);

            const videoId = getBBBVideoId(targetVideo);
            const timestamps = await getTimestamps(
              code,
              validatedBook,
              videoId,
              targetVideo?.bbb_id,
              targetVideo?.chapter
            );
            const hasVerseZero = hasIntroSegment(timestamps, chapterNumber, targetVideo?.bbb_id);

            if (hasVerseZero && introOwner !== undefined) {
              const chapterVideos = lut.videosInChapter(validatedBook, validatedChapter) ?? [
                introOwner,
              ];
              verseForUrl = introUrlVerseForVideo(introOwner, validatedChapter, chapterVideos);

              const ownerFirstVerse = firstVerseNumber(introOwner, validatedChapter);
              if (ownerFirstVerse !== undefined && ownerFirstVerse !== '') {
                selectionVerse = ownerFirstVerse;
              } else {
                const [, fallbackOwnerVerse] = getFirstChapterVerse(introOwner);
                if (fallbackOwnerVerse !== '') {
                  selectionVerse = fallbackOwnerVerse;
                }
              }
            } else if (hasVerseZero) {
              verseForUrl = '0';
            }
          } catch (error) {
            // Keep validated verse when timestamp fetch fails.
          }
        }

        if (validatedBook !== bookOrSlug || validatedChapter !== chapter || verseForUrl !== verse) {
          history.replace(`/${code}/${validatedBook}.${validatedChapter}.${verseForUrl}`);
          return;
        }

        dispatch(setExpandedItem(validatedBook));
        dispatch(setSelectedTestament(isNewTestament(validatedBook) ? 'new' : 'old'));
        dispatch(setSelectedBook(validatedBook));
        dispatch(
          setSelectedChapterVerse({
            chapter: validatedChapter,
            verse:
              verseForUrl === '0' || isSegmentIntroVerse(verseForUrl)
                ? selectionVerse
                : verseForUrl,
          })
        );
        setLastURL(
          `/${code}/${validatedBook}.${validatedChapter}.${verseForUrl}${window.location.search}`
        );
      };

      syncBBBParams().catch(() => undefined);
    }
    return undefined;
  }, [
    activeBook,
    activeChapter,
    activeVerse,
    bookOrSlug,
    chapter,
    code,
    dispatch,
    history,
    isOnBlacklist,
    lut,
    selectedTranslationType,
    verse,
  ]);
};
